const common = {
  nav: {
    about: 'About',
    services: 'Services',
    credentials: 'Credentials',
    contact: 'Contact',
    insights: 'Insights',
    cta: 'Book a Consultation',
  },
  footer: {
    badge: 'Clinical Group Analyst',
    tagline: 'Bridging deep psychological insight with practical organisational reality. Based in Tirana, serving clients internationally.',
    navTitle: 'Navigation',
    about: 'About Me',
    services: 'Services Offered',
    credentials: 'Credentials',
    contact: 'Contact',
    affiliations: 'Professional Affiliations',
    iga: 'Institute of Group Analysis (IGA)',
    igaProgram: 'UK International Program',
    location: 'Tirana, Albania',
    languages: 'English · Italian · Albanian',
    copyright: '© 2026 Mirela. All rights reserved.',
  },
  about: {
    hero: {
      badge: 'Consultant & Psychotherapist\nSystems, Groups, and Organizational Dynamics',
      heading: 'Hello, my name is Mirela Bregu. I am an organizational consultant and a certified group therapist.',
      intro: 'My practice is built on decades of experience at the intersection of people, business, human systems, and organizations. Serving clients in Tirana and internationally.',
      cta1: 'Get in Touch',
      cta2: 'View Services',
      stat1: 'Years Experience',
      stat2: 'Languages Served',
      stat3: 'Certified',
      locationLabel: 'Practice Location',
      locationValue: 'Tirana, Albania',
    },
    bg: {
      badge: 'Background',
      headline: 'Decades at the intersection of <italic>business & human behaviour</italic>',
      p1: 'My career has been defined by a deep interest in how people function within structured environments. I have spent decades navigating the complexities of business development, project coordination, and organisational management. These are roles that require a keen understanding of human behaviour, communication, and systemic health.',
      p2: "I don't see individuals in isolation; I see the networks, histories, and groups that shape them. This systemic lens informs every aspect of my practice.",
      foundationBadge: 'The Foundation',
      foundationText: '<p>My work with individuals and groups is underpinned by a rigorous academic and clinical background: an MBA from the University of Nebraska and a specialized training with the Institute of Group Analysis (IGA), UK.</p><p> I’ve always been fascinated by the patterns that shape how we show up - not just in our careers, but in our closest relationships and our daily lives. My work is about bridging the gap between deep psychological insight and the messy, real-world stuff we face every day. </p><p>Based in the heart of Tirana, I work with a diverse mix of people in <bold>English, Italian, and Albanian</bold>. I want my practice to be internationally informed and locally grounded.</p> <italic><p>\'A space where you can turn old habits into new resilience, no matter where you’re starting from.\'</p><italic>',
      verified: 'MBA · Group Analyst · IGA Certified',
      practiceBadge: 'Practice',
      practiceText: 'My practice is based in Tirana, where I serve a diverse clientele in English, Italian, and Albanian. It is a space that is internationally informed but locally grounded.',
    },
    cred: {
      iga: 'Institute of Group Analysis (IGA), UK',
      languages: 'English · Italian · Albanian',
      location: 'Based in Tirana, Albania',
    },
  },
  services: {
    hero: {
      badge: 'What I Offer',
      title: 'Services Offered',
      subtitle: 'Starting therapy or consultancy is a significant step toward self-understanding. Whether you are an individual, a couple, or an organisation, I offer a space that is rigorous, compassionate, and grounded in evidence.',
    },
    sectionBadge: 'Core Specialisms',
    c1Title: 'Group Psychoanalytic Psychotherapy',
    c1Desc: 'Weekly sessions for individuals seeking to explore their relational patterns within a supportive group setting. The group becomes a microcosm of the social world, a place to understand oneself through others.',
    c2Title: 'Individual Psychodynamic Therapy',
    c2Desc: 'Focused one-on-one work addressing personal transitions, identity, and emotional well-being.',
    c3Title: 'Organisational Dynamics Supervision',
    c3Desc: 'Specialised support for leaders and teams to improve group cohesion, communication, and organisational health through a systems-psychodynamic lens.',
    c4Title: 'Leadership & Reflection',
      c4Desc: 'A confidential space for executives to process the psychological weight of leadership, strategic responsibility, and the isolation that often accompanies decision-making. To ensure our work leads to practical growth, I integrate specialized tools like International Preferences Indicator (IPI) and The International Profiler (TIP) to help refine your leadership style and intercultural effectiveness.',
    c5Title: 'Supervision & Mentorship',
    c5Desc: 'True professional growth isn’t just about moving up, it is about expanding one’s capacity to lead, think, and work.',
    dir: {
      badge: 'Full Directory',
      offered: 'Services Offered',
      worksWith: 'Works With',
      areas: 'Areas of Expertise',
      profTitle: 'Professional Title',
      groupAnalyst: 'Consultant & Group Analyst',
      servicesList: ['Coaching', 'Conflict Management', 'Consultation', 'Corporate Workshops', 'Group Therapy', 'Individual Psychotherapy', 'Online Therapy', 'Relationship Counselling', 'Supervision & Training', 'Support Groups'],
      workList: ['Adults', 'Communities', 'Couples', 'Elderly', 'Families', 'Groups', 'Organisations', 'Parents', 'Individuals'],
      areasList: ['Anxiety', 'Career Issues', 'Cross-Cultural', 'Divorce Coping', 'Family Issues', 'Guilt or Forgiveness', 'Life Transitions', 'Parenting Support', 'Stress / Burnout', 'Self-Esteem'],
    },
    fees: {
      badge: 'Fees & Session Info',
      headline: 'Clear, flexible, and accessible',
      subtext: 'Taking the first step toward self-understanding or institutional health is a major milestone. If you are looking for <bold>professional support</bold> that respects both the individual experience and the wider system, I invite you to reach out for an initial consultation to explore the best path forward.',
      type1Label: 'Individual Psychodynamic Therapy',
      type1Duration: '50-minute sessions',
      type1Frequency: 'Weekly or bi-weekly',
      type1Format: 'In-person (Tirana) or online',
      type2Label: 'Group Psychoanalytic Psychotherapy',
      type2Duration: '90-minute sessions',
      type2Frequency: 'Weekly — ongoing group',
      type2Format: 'In-person (Tirana) or online',
      type3Label: 'Organisational Consultation',
      type3Duration: 'Bespoke session length',
      type3Frequency: 'Tailored to project scope',
      type3Format: 'On-site or online',
      type4Label: 'Leadership Reflective Practice',
      type4Duration: '60-minute sessions',
      type4Frequency: 'Weekly or fortnightly recommended',
      type4Format: 'In-person or online',
      feeNoticeTitle: 'A note on fees',
      feeNoticeText: 'Sessions are structured to fit your circumstances. Fees are discussed openly during an initial consultation. I believe financial concerns should never be a barrier to support.',
      feeNoticeNote: 'Sliding scale arrangements may be available in certain cases. Please enquire directly.',
      introTitle: 'Free Initial Consultation',
      introText: 'A complimentary 30-minute introductory consultation to discuss your needs, ask questions, and explore whether my approach is the right fit for you. The consultation can be held in presence or online.',
      introBtn: 'Book your free call',
      onlineTitle: 'Available Online',
      onlineText: 'All individual and coaching sessions are available via secure video call for clients across Europe and internationally.',
    },
    cta: {
      title: 'Ready to Begin?',
      desc: 'If you are looking for an expert who understands the complex interplay between the individual and the group, I invite you to reach out for an initial consultation.',
      btn: 'Arrange a Consultation',
    },
  },
  contact: {
    hero: {
      badge: 'Reach Out',
      title: 'Get in Touch',
      subtitle: 'Ask a quick question or arrange a time to meet. I aim to respond within 48 hours.',
    },
    form: {
      name: 'Full Name',
      namePlaceholder: 'Your name',
      email: 'Email Address',
      emailPlaceholder: 'you@example.com',
      phone: 'Phone Number',
      phoneOptional: '(optional)',
      phonePlaceholder: '+355 XX XXX XXXX',
      service: 'Service of Interest',
      serviceDefault: 'Select a service…',
      service1: 'Group Psychoanalytic Psychotherapy',
      service2: 'Individual Psychodynamic Therapy',
      service3: 'Organisational Dynamics',
      service4: 'Leadership Coaching and Reflective Practice',
      service5: 'Supervision & Mentorship',
      service6: 'Not sure yet',
      preferred: 'Preferred Contact Method',
      contactEmail: 'Email',
      contactPhone: 'Phone',
      contactEither: 'Either',
      message: 'Message',
      messagePlaceholder: 'Ask a question, or tell me a little about what you are looking for…',
      submit: 'Send Message',
      sending: 'Sending…',
      privacy: 'Your information is confidential and will not be shared.',
      successTitle: 'Message Sent',
      successDesc: 'Thank you for reaching out. I will be in touch with you shortly to arrange a time to meet.',
      successBtn: 'Send Another Message',
      error: 'Something went wrong. Please try again or contact me directly.',
    },
    info: {
      email: 'Email',
      phone: 'Phone',
      location: 'Location',
      langTitle: 'Languages',
      availTitle: 'Availability',
      availText: 'Sessions available in person in Tirana and online for international clients. Initial consultations are available by appointment.',
    },
    loc: {
      badge: 'Practice Location',
      title: 'Based in Tirana, serving clients internationally',
      text: 'The practice is located in Tirana, Albania. Online therapy and consultation are available for clients across Europe and beyond. All sessions are strictly confidential.',
    },
  },
  coreCompetencies: {
    badge: 'Core Competencies',
    quote: "We don't live or work in isolation; we exist within 'constellations, our families, our teams, and within our organizations.",
    paragraphTitle: "The \"Insight to Action\" Approach",
    paragraph: "<p>Whether I am sitting in my private practice with an individual or advising an executive board, the aim is to bridge the gap between human psychology and operational reality. Challenges like burnout, team conflict, or chronic anxiety are signals of deeper, unaddressed issues. While my clinical training helps uncover the unseen dynamics driving these behaviours, my MBA ensures we ground our work in reality.</p> <p>Insight alone isn't enough; together we use that understanding to design practical, structural solutions. Treating just the immediate symptom isn’t enough to build a richer, more authentic, and functional way of operating in life.</p>",
    c1Title: 'Individual & Group Psychotherapy ',
    c1Desc: 'Providing a safe clinical space to explore relational patterns, anxiety, and personal growth. Together, we look beneath the surface to uncover the root of the struggle, building genuine, lasting resilience.',
    c2Title: 'Building Healthy Institutions',
    c2Desc: 'Helping leadership teams communicate effectively and build cultures rooted in fairness and merit. By understanding group dynamics, I help ensure an organization’s structure supports the people working within it.',
    c3Title: 'Financial & Operational Growth',
    c3Desc: 'Guiding teams through the growing pains of expanding financial operations. Drawing on years of experience with the ProCredit and Intesa Sanpaolo models, I help organizations grow their commercial lines sustainably, without losing their core integrity.',
    c4Title: 'Navigating Organizational Change ',
    c4Desc: 'Supporting institutions through major shifts in culture or process. Change is difficult for any system; my focus is on making sure the human element remains central, rather than getting lost in the transition.',
    c5Title: 'Leadership Coaching & Support  ',
      c5Desc: 'Working closely with executives to help them manage the intense pressures of their roles. By understanding their own needs and the needs of their teams, leaders can foster true cohesion rather than forced compliance',
  },
  education: {
    badge: 'Education',
    certsBadge: 'Advanced Certifications',
    academicBadge: 'Academic & Volunteer Experience',
    academicText: 'Early in my career, I served as a Seminar Leader at the University of Tirana, teaching Basis of Marketing and International Business, before going on to chair the Board of Junior Achievement Albania. I subsequently led lectures & seminars in Credit Management and Bank Simulation at FASTIP, Aleksander Moisiu University. I am a founding member of Group Analysis Albania (member of EGATIN), acted as Treasurer for several years and currently serving as General Secretary of this non-profit organisation. I am a full member of Group Analytic Society International (GASI)..',
    d1Degree: 'Qualified Group Analyst (QGA)',
    d1Institution: 'Institute of Group Analysis (IGA)',
    d1Location: 'London',
    d1Period: '2019 – 2024',
    d2Degree: 'Master of Business Administration (MBA)',
    d2Institution: 'University of Nebraska-Lincoln / University of Tirana',
    d2Period: '2002 – 2004',
    d3Degree: "Bachelor's Degree in Finance",
    d3Institution: 'University of Tirana',
    d3Period: '1991 – 1995',
    cert1Title: 'The International Profiler (TIP – next gen)',
    cert1Issuer: 'Global Agility Tools',
    cert2Title: 'International Preferences Indicator (IPI next gen)',
    cert2Issuer: 'Global Agility Tools',
    cert3Title: 'Digital Business & Entrepreneurial Toolbox',
    cert3Issuer: 'EBRD',
    cert4Title: 'Change Management for Consulting',
    cert4Issuer: 'EBRD',
    cert5Title: 'Foundation Course in Group Analysis',
    cert5Issuer: 'IGA Accredited',
    cert6Title: 'Harvard ManageMentor',
    cert6Issuer: 'Harvard Business Publishing',
  },
  experience: {
    badge: 'Working Experience',
    j1Title: 'Consultant (Albania)',
    j1Org: 'FBI Consulting ShPK',
    j1Period: 'October 2017 – Present',
    j1b1: 'Managed organizational restructuring and change management, optimizing job descriptions and incentive/bonus systems.',
    j1b2: 'Assisted Startups and SMEs with adapting business strategy, understanding customer base, and re-engineering internal workflows and processes between different functional departments.',
    j1b3: 'Employed Group-analysis methodology to facilitate dialogue and organizational health. Provided psychotherapy/coaching to individuals and groups.',
    j2Title: 'Head of Retail Banking Division (Albania)',
    j2Org: 'Tirana Bank – PIRAEUS BANK Group',
    j2Period: 'November 2013 – June 2017',
    j2b1: 'Developed and implemented the Retail Strategy and Action-Plans, managing all retail banking activities: Products & Services, Cards & Electronic-channels, and Marketing.',
    j2b2: 'Re-engineered processes and workflows through product mix optimization and introduction of incentives to boost sales.',
    j2b3: 'Evaluated procedures and processes at Branch and Division levels to ensure efficiency, effectiveness, and compliance with internal policies and regulatory requirements.',
    j3Title: 'Executive Director (Uganda)',
    j3Org: 'PEARL MICROFINANCE',
    j3Period: 'July 2012 – March 2013',
    j3b1: 'Led the turnaround project of the institution (14 branches, 140 employees) facing limited resources, achieving break-even within a 4-month timeframe.',
    j3b2: 'Improved institutional culture and led the reorganization/creation of several key departments (legal, administration, operations).',
    j3b3: 'Built capacity by introducing training programs, incentive payment schemes, and implementing mobile money payment systems.',
    j3b4: 'Successfully implemented a new MIS with detailed processes/procedures and proper data migration.',
    j4Title: 'Senior Consultant, Principal (Projects in Pacific Islands, Uganda, Ghana)',
    j4Org: 'A2F Consulting',
    j4Period: 'October 2011 – June 2012',
    j4b1: 'Provided strategy, advisory, and operational consulting focusing on process re-engineering, change management, and product mix optimization.',
    j4b2: 'Implemented tools for liquidity management, loan monitoring, and credit risk management, and developed department reporting frameworks.',
    j5Title: 'Manager-Coordinator of Retail Team (Bank of Qingdao Project, China)',
    j5Org: 'INTESA SANPAOLO S.p.A., ISBD',
    j5Period: '2009 – 2011',
    j5b1: 'Provided know-how and capacity building to foster the Retail Business Development of the Bank of Qingdao (BQD).',
    j5b2: 'Revised, differentiated, and enriched the basic product catalog by analyzing customer base and usage rates.',
    j5b3: 'Designed the appropriate Retail Service Model and led the development of the internal structure from simple transaction offering to full service/advisory.',
    j5b4: 'Led the development of the common branch layout model, basic branch revamping, and standardized the process for BTL marketing materials.',
    j6Title: 'Consultant, Retail Business Development Office',
    j6Org: 'INTESA SANPAOLO S.p.A., ISBD (Italy)',
    j6Period: '2008 – 2009',
    j6b1: 'Coordinated Retail activities and strategies across the International Subsidiary Banks Division (ISBD) scope of project.',
    j6b2: "Supported the launch of innovative products/services and propagated ISP Group's Best Practices.",
    j7Title: 'Manager, Network Expansion',
    j7Org: 'INTESA SANPAOLO Bank Albania (former ABA, Albania)',
    j7Period: '2004 – 2007',
    j7b1: 'Responsible for all aspects of planning, coordinating, and ensuring successful bank expansion.',
    j7b2: 'Managed Geo-Market study, negotiated legal contracts, supervised construction, and coordinated all internal departments and third parties (local government, Central Bank).',
    j8Title: 'Earlier Career',
    j8Org: 'INTESA SANPAOLO Bank Albania',
    j8Period: '2002 – 2008',
    j8b1: 'Held roles as: Manager Statistics and Reporting Card, Services Department; Supervisor, Compliance Operations; Internal Audit.',
    j9Title: 'Earlier Career',
    j9Org: 'PROCREDIT Group (Albania)',
    j9Period: '1996 – 2002',
    j9b1: 'Held roles as: Regional Manager, Micro LO, SME LO, and Branch Manager.',
    j9b2: 'Coordinated, controlled, and decided on all operational issues within the region.',
    j9b3: 'Planned, selected, and evaluated regional staff, and ensured the achievement of targets set by management.',
  },
  insights: {
    hero: {
      badge: 'Reflections & Writing',
      title: 'Insights',
      subtitle: 'Reflections on group dynamics, organisational life, and the inner workings of the mind — originally shared on LinkedIn.',
      linkedinCta: 'Follow on LinkedIn',
    },
    featuredLabel: 'Featured',
    readMore: 'Read more',
    minRead: 'min read',
    filterAll: 'All',
    empty: 'No posts found in this category.',
    watchVideo: 'Watch video',
    includesVideo: 'Includes video',
    viewOnLinkedIn: 'View original post on LinkedIn',
    ctaTitle: 'More on LinkedIn',
    ctaSubtext: 'Follow Mirela on LinkedIn to receive new reflections and professional updates as they are published.',
    ctaBtn: 'Follow on LinkedIn',
  },
};

export default common;
